import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "iryaa_cart_v1";
const lineKey = (productId, size) => (size ? `${productId}|${size}` : productId);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || "[]").map((i) => ({ ...i, key: i.key || lineKey(i.product_id, i.size) }));
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, quantity = 1, variant = null) => {
    const key = lineKey(product.id, variant?.size);
    const cap = (q) => (variant ? Math.min(q, variant.stock) : q);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: cap(i.quantity + quantity) } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          image: product.image,
          price: variant ? variant.price : product.price_from,
          size: variant?.size || null,
          stock: variant ? variant.stock : null,
          buy_url: product.buy_url || null,
          quantity: cap(quantity),
        },
      ];
    });
    setIsOpen(true);
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const setQty = (key, quantity) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: i.stock != null ? Math.min(quantity, i.stock) : quantity } : i))
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  const { subtotal, shipping, total, count } = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = subtotal === 0 ? 0 : subtotal >= 1000 ? 0 : 100;
    return { subtotal, shipping, total: subtotal + shipping, count: items.reduce((s, i) => s + i.quantity, 0) };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, add, remove, setQty, clear, subtotal, shipping, total, count, isOpen, setIsOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
